import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useApi } from "@/hooks/useApi";
import type { ImputacionResumen } from "@/@types/Api_Extracted";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { DataTable } from "@/components/ui/datatable";
import { CommonFeatures, EMPTY_ARRAY } from "@/hooks/useTanstackTable";

import { Paginator } from "@/components/ui/paginator";
import { isInstantBetween, convertToBest } from "@/lib/utils";
import { createColumnHelper, tableFeatures, useTable } from "@tanstack/react-table";

interface ListTableProps {
	readonly filter?: {
		range?: {
			from?: Temporal.PlainDate;
			to?: Temporal.PlainDate;
		};
	};
}
export function ListTable(props: ListTableProps) {
	const navigate = useNavigate();

	const api = useApi();
	const query = api.useQuery("get", "/v1/Empresa/Imputaciones", {});

	const handleNewInput = () => {
		void navigate("input/new");
	};

	const { columns, features } = useMemo(() => Columns(), []);
	const table = useTable({
		features,
		columns,
		data: query.data ?? EMPTY_ARRAY,
		globalFilterFn: (row, columnId, filterValue) => filterRow(row.original, filterValue as ListTableProps["filter"]),
	});
	const setGlobalFilter = table.setGlobalFilter;

	useEffect(() => {
		const propsFilter = props.filter;
		if (propsFilter?.range) {
			setGlobalFilter((oldFilter: ListTableProps["filter"] | null) => {
				const newFilter = structuredClone(oldFilter ?? {});
				newFilter.range = propsFilter.range;
				return newFilter;
			});
		}
	}, [props.filter, setGlobalFilter]);

	return (
		<>
			<DataTable
				table={table}
				noResultsMessage="Aún no hay imputaciones registradas"
				errorMessage="Error al cargar las imputaciones"
				isLoading={query.isPending}
				isError={query.isError}
			/>
			<div className="flex items-center justify-between mt-4">
				<div>
					{table.getPageCount() != 0 && (
						<Paginator
							pageCount={table.getPageCount()}
							pageIndex={table.state.pagination.pageIndex}
							shownPages={5}
							onPageChange={table.setPageIndex}
							canPrevious={table.getCanPreviousPage()}
							canNext={table.getCanNextPage()}
						/>
					)}
				</div>
				<Button variant="primary" onClick={handleNewInput}>
					Nueva imputación
				</Button>
			</div>
		</>
	);
}

function Columns() {
	const formatter = new Intl.DateTimeFormat("es-ES", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	});
	const formatDate = (value: string) => formatter.format(Temporal.Instant.from(value));

	const columnHelper = createColumnHelper<typeof features, ImputacionResumen>();

	const features = tableFeatures(CommonFeatures);

	const columns = columnHelper.columns([
		columnHelper.accessor("desde", {
			header: "Desde",
			size: -1,
			cell: (props) => formatDate(props.getValue()),
		}),
		columnHelper.accessor("hasta", {
			header: "Hasta",
			size: -1,
			cell: (props) => formatDate(props.getValue()),
		}),
		columnHelper.accessor("lineas", {
			id: "referenciasCount",
			header: "Nº Referencias",
			size: -1,
			cell: (props) => <TooltipReferencias referencias={props.getValue()} />,
		}),
		columnHelper.accessor((row) => getGramosReferencias(row.lineas), {
			id: "gramosReferencias",
			header: "CO2 Equivalente",
			size: -1,
			cell: (props) => convertToBest(props.getValue(), "g"),
		}),
		columnHelper.display({
			id: "acciones",
			header: "Acciones",
			size: -1,
			cell: (props) => <AccionesFila fila={props.row.original} />,
		}),
	]);

	return { columns, features };
}

function TooltipReferencias(props: {
	readonly referencias: ImputacionResumen["lineas"];
}) {
	const referenciasTooltip = Array.from(props.referencias)
		.slice(0, 3)
		.map((ref) => ref.descripcionReferencia);

	if (props.referencias.length > 3) {
		referenciasTooltip.push(`y ${props.referencias.length - 3} más`);
	}

	// return <span>{props.referencias.length} referencias</span>;
	return (
		<Tooltip>
			<TooltipTrigger>{props.referencias.length} referencias</TooltipTrigger>
			<TooltipContent>
				<p>{referenciasTooltip.join(", ")}</p>
			</TooltipContent>
		</Tooltip>
	);
}
function AccionesFila(props: { readonly fila: ImputacionResumen; }) {
	const navigate = useNavigate();
	const handleEdit = () => {
		void navigate("input/" + props.fila.idImputacion);
	};

	return (
		<div className="grid grid-cols-1 gap-2">
			<Button variant="secondary" onClick={handleEdit}>
				Editar
			</Button>
		</div>
	);
}

/** Reduce el listado de referencias y devuelve su suma de CO2 equivalente */
function getGramosReferencias(referencias: ImputacionResumen["lineas"]) {
	return Math.abs(
		Object.values(referencias).reduce((acc, ref) => {
			return acc + ref.conversion_equivalente_kilos * ref.cantidad * 1000;
		}, 0),
	);
}

function filterRow(rowValue: ImputacionResumen, filterValue: ListTableProps["filter"]) {
	if (filterValue?.range?.from || filterValue?.range?.to) {
		const { from, to } = filterValue.range;

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const filtroDesde = from?.toZonedDateTime({
			timeZone,
			plainTime: "00:00:00",
		});
		const filtroHasta = to?.toZonedDateTime({
			timeZone,
			plainTime: "23:59:59",
		});

		const rowDesde = Temporal.Instant.from(rowValue.desde);
		const rowHasta = Temporal.Instant.from(rowValue.hasta);

		if (isInstantBetween(rowDesde, filtroDesde ?? null, filtroHasta ?? null))
			return true; // Show row if it falls within the date range

		if (isInstantBetween(rowHasta, filtroDesde ?? null, filtroHasta ?? null))
			return true; // Show row if it falls within the date range

		return false; // Hide row if it does not fall within the date range
	}
	return true; // If no filter is applied, return true to show the row
}
